import { Badge, DropdownBtn, Table } from '@/src/components/common'
import { Modal } from '@/src/components/Modals'
import { ProductInventoryResMock } from '@/src/types/inventory.type'
import { ProductAttributes } from '@/src/types/product.type'
import { PHARMACY_MODAL_TYPE, useModalStore } from '@/src/zustand/Modal'
import { DropDownIcon, FilterIcon, TriDot } from '@icons'
import { Box, ColumnConfig, Text } from 'grommet'
import { useMemo, useState } from 'react'

type Props = {
  onClose: () => void
  defaultData?: ProductInventoryResMock[]
  id?: string
  isLoading?: boolean
  productData?: ProductAttributes
}
const ShowStock = ({ onClose, defaultData, isLoading, productData }: Props) => {
  const [selectedLocation, setSelectedLocation] = useState<string>('all')
  const { openModal } = useModalStore()

  const locations = useMemo(() => {
    if (!Array.isArray(defaultData)) return []
    const set = new Set<string>()
    defaultData.forEach(inv => set.add(inv.attributes.stock_location || 'Unknown'))
    return Array.from(set)
  }, [defaultData])

  const filteredStock = useMemo(() => {
    if (!Array.isArray(defaultData)) return []
    if (selectedLocation === 'all') return defaultData
    return defaultData.filter(inv => (inv.attributes.stock_location || 'Unknown') === selectedLocation)
  }, [defaultData, selectedLocation])

  const columns: ColumnConfig<ProductInventoryResMock>[] = [
    {
      header: (
        <Text size="small" weight={500}>
          Strength
        </Text>
      ),
      property: 'strength',
      render: datum => {
        if (datum.attributes?.strength) return <Text size="small">{datum.attributes.strength}</Text>

        const apiStrengths = datum.attributes?.api_strengths
        const strengths =
          apiStrengths && apiStrengths?.length > 0
            ? apiStrengths
                .reverse()
                ?.map(api => api.strength)
                .filter(Boolean)
                .join('/')
            : '-'
        return <Text size="small">{strengths || '-'}</Text>
      },
      sortable: true
    },
    {
      header: (
        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
          Total Quantity
        </Text>
      ),
      property: 'stock_quantity',
      render: datum => (
        <Text size="small" style={{ textTransform: 'capitalize' }}>
          {typeof datum.attributes.stock_quantity === 'number' ? datum.attributes.stock_quantity : 0}
        </Text>
      ),
      sortable: false
    },
    {
      header: (
        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
          Batches
        </Text>
      ),
      property: 'stock_quantity',
      render: datum => (
        <Text size="small" style={{ textTransform: 'capitalize' }}>
          {typeof datum.attributes.stock_quantity === 'number' ? datum.attributes.stock_quantity : 0}
        </Text>
      ),
      sortable: false
    },
    {
      header: (
        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
          Unit Cost
        </Text>
      ),
      property: 'unit_cost',
      render: datum => (
        <Text size="small" style={{ textTransform: 'capitalize' }}>
          {datum.attributes.unit_cost
            ? `₦ ${Number(datum.attributes.unit_cost).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '-'}
        </Text>
      ),
      sortable: false
    },
    {
      header: (
        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
          Expiry Date
        </Text>
      ),
      property: 'expiry_date',
      render: datum => {
        const val = datum.attributes.expiry_date
        if (!val)
          return (
            <Text size="small" style={{ textTransform: 'capitalize' }}>
              -
            </Text>
          )
        const d = new Date(val)
        if (isNaN(d.getTime()))
          return (
            <Text size="small" style={{ textTransform: 'capitalize' }}>
              -
            </Text>
          )
        return (
          <Text size="small" style={{ textTransform: 'capitalize' }}>
            {d.toLocaleDateString()}
          </Text>
        )
      },
      sortable: false
    },
    {
      header: (
        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
          Status
        </Text>
      ),
      property: 'status',
      render: datum => (
        <Box direction="row" gap="xsmall">
          <Badge
            label={
              datum.attributes.status === 'in_stock'
                ? 'In Stock'
                : datum.attributes.status === 'out_of_stock'
                  ? 'Out Of Stock'
                  : (datum.attributes.status ?? '-')
            }
            size="small"
            status={datum.attributes.status ?? '-'}
          />
        </Box>
      ),
      sortable: false
    },
    {
      property: 'actions',
      render: datum => (
        <DropdownBtn
          dropdownLabel={<TriDot />}
          options={[
            {
              handleClick: (onClose: () => void) => {
                openModal(PHARMACY_MODAL_TYPE.INVENTORY_DETAILS, { id: datum?.id })
                onClose()
              },
              label: 'View Details'
            },
            {
              handleClick: (onClose: () => void) => {
                openModal(PHARMACY_MODAL_TYPE.ADD_STOCK, { defaultData: datum.attributes, id: datum?.id })
                console.log(datum)
                onClose()
              },
              label: 'Add Batch'
            }
            // {
            //   handleClick: (onClose: () => void) => {
            //     // openModal(PHARMACY_MODAL_TYPE.UPDATE_PRODUCT, { defaultData: datum.attributes, id: datum?.id });
            //     onClose()
            //   },
            //   label: 'Adjust stock'
            // },
            // {
            //   handleClick: (onClose: () => void) => {
            //     // openModal(PHARMACY_MODAL_TYPE.TRANSFER_STOCK, { defaultData: datum.attributes, id: datum?.id });
            //     onClose()
            //   },
            //   label: 'Transfer stock'
            // }
          ]}
        />
      )
    }
  ]

  const dropDown = (
    <DropdownBtn
      dropdownLabel={
        <Box align="center" direction="row" gap="4px" justify="center">
          <Text size="small" style={{ textTransform: 'capitalize' }}>
            {selectedLocation === 'all' ? 'All Locations' : selectedLocation}
          </Text>
          <DropDownIcon />
        </Box>
      }
      options={[
        {
          handleClick: (onClose: () => void) => {
            setSelectedLocation('all')
            onClose()
          },
          label: 'All Locations'
        },
        ...locations?.map(loc => ({
          handleClick: (onClose: () => void) => {
            setSelectedLocation(loc)
            onClose()
          },
          label: loc
        }))
      ]}
      style={{
        border: `1px solid rgba(0, 0, 0, 0.33)`,
        borderRadius: '100px',
        minWidth: '123px',
        padding: '6px 12px'
      }}
    />
  )

  const FilterBtn = () => (
    <Box
      align="center"
      border={{ size: '1px' }}
      direction="row"
      gap="10px"
      pad="small"
      round="100px"
      style={{ cursor: 'pointer' }}
    >
      <FilterIcon />
      <Text size="14px">Filter</Text>
    </Box>
  )

  return (
    <Modal
      onClose={onClose}
      style={{
        width: '75vw'
      }}
      title={`${productData?.generic_name}(${productData?.brand_name}) Stock List`}
    >
      <Box pad="medium" style={{ overflowX: 'hidden', overflowY: 'auto' }}>
        <Table
          columns={columns}
          data={filteredStock}
          dataIsLoading={isLoading}
          dropDown={dropDown}
          filter={<FilterBtn />}
        />
      </Box>
    </Modal>
  )
}

export default ShowStock
